import {
    BodySmallEmphasized,
    BodySmallRegular,
    XStack,
    YStack,
} from 'smoothie';

interface SpanData {
    spanId: string;
    parentSpanId?: string | null;
    name: string;
    spanType?: string;
    startedAt?: Date | string | null;
    endedAt?: Date | string | null;
    error?: unknown;
}

interface TraceDetailViewProps {
    spans: SpanData[];
    selectedSpanId: string | null;
    onSelectSpan: (spanId: string) => void;
}

interface HierarchicalSpan extends SpanData {
    children: HierarchicalSpan[];
    depth: number;
    descendantCount: number;
}

const SPAN_TYPE_COLORS: Record<string, string> = {
    agent_run: '#2563eb',
    llm_call: '#16a34a',
    tool_call: '#9333ea',
    mcp_tool_call: '#9333ea',
    memory_operation: '#ea580c',
    processor_run: '#475569',
    workflow_run: '#2563eb',
    workflow_step: '#2563eb',
    generic: '#475569',
};

const getSpanColor = (spanType?: string): string =>
    SPAN_TYPE_COLORS[spanType ?? 'generic'] ?? '#475569';

export const TraceDetailView = ({
    spans,
    selectedSpanId,
    onSelectSpan,
}: TraceDetailViewProps) => {
    const hierarchicalSpans = buildHierarchy(spans);
    const traceStartTime = getTraceStartTime(spans);
    const traceEndTime = getTraceEndTime(spans);
    const totalDuration =
        traceStartTime != null && traceEndTime != null
            ? traceEndTime - traceStartTime
            : 0;

    return (
        <YStack gap={2}>
            {hierarchicalSpans.map((span) => (
                <SpanRow
                    key={span.spanId}
                    span={span}
                    isSelected={selectedSpanId === span.spanId}
                    selectedSpanId={selectedSpanId}
                    onSelect={onSelectSpan}
                    traceStartTime={traceStartTime ?? 0}
                    totalDuration={totalDuration}
                />
            ))}
        </YStack>
    );
};

const SpanRow = ({
    span,
    isSelected,
    selectedSpanId,
    onSelect,
    traceStartTime,
    totalDuration,
}: {
    span: HierarchicalSpan;
    isSelected: boolean;
    selectedSpanId: string | null;
    onSelect: (spanId: string) => void;
    traceStartTime: number;
    totalDuration: number;
}) => {
    const startMs =
        span.startedAt != null
            ? new Date(span.startedAt).getTime() - traceStartTime
            : 0;
    const endMs =
        span.endedAt != null
            ? new Date(span.endedAt).getTime() - traceStartTime
            : totalDuration;
    const duration = endMs - startMs;
    const leftPct = totalDuration > 0 ? (startMs / totalDuration) * 100 : 0;
    const widthPct =
        totalDuration > 0
            ? Math.max((duration / totalDuration) * 100, 1.5)
            : 100;
    const hasError = span.error != null;
    const spanColor = hasError ? '#dc2626' : getSpanColor(span.spanType);

    return (
        <>
            <XStack
                paddingVertical={6}
                paddingLeft={12 + span.depth * 20}
                paddingRight={12}
                borderRadius={6}
                cursor="pointer"
                backgroundColor={isSelected ? '$gray200' : 'transparent'}
                hoverStyle={{ backgroundColor: '$gray100' }}
                onPress={() => {
                    onSelect(span.spanId);
                }}
                gap={10}
                alignItems="center"
                minHeight={32}
            >
                {span.depth > 0 && (
                    <BodySmallRegular
                        color="$gray1000"
                        style={{ fontSize: 11, lineHeight: 14 }}
                    >
                        └
                    </BodySmallRegular>
                )}

                <YStack width={200} flexShrink={0}>
                    <BodySmallRegular
                        numberOfLines={1}
                        color={hasError ? '$red1100' : '$gray1200'}
                    >
                        {span.name}
                    </BodySmallRegular>
                </YStack>

                {span.descendantCount > 0 && (
                    <BodySmallRegular color="$gray1000">
                        {span.descendantCount} ⊙
                    </BodySmallRegular>
                )}

                <YStack flex={1} height={16} justifyContent="center">
                    <YStack
                        height={10}
                        backgroundColor="$gray200"
                        borderRadius={5}
                        overflow="hidden"
                        position="relative"
                    >
                        <YStack
                            position="absolute"
                            top={0}
                            bottom={0}
                            left={`${leftPct}%`}
                            width={`${widthPct}%`}
                            backgroundColor={spanColor}
                            borderRadius={5}
                        />
                    </YStack>
                </YStack>

                <BodySmallEmphasized
                    color="$gray1200"
                    style={{ fontFamily: 'monospace', fontSize: 12 }}
                    width={70}
                    textAlign="right"
                >
                    {formatDuration(duration)}
                </BodySmallEmphasized>
            </XStack>

            {span.children.map((child) => (
                <SpanRow
                    key={child.spanId}
                    span={child}
                    isSelected={selectedSpanId === child.spanId}
                    selectedSpanId={selectedSpanId}
                    onSelect={onSelect}
                    traceStartTime={traceStartTime}
                    totalDuration={totalDuration}
                />
            ))}
        </>
    );
};

function buildHierarchy(spans: SpanData[]): HierarchicalSpan[] {
    const spanMap = new Map<string, HierarchicalSpan>();
    const roots: HierarchicalSpan[] = [];

    for (const span of spans) {
        spanMap.set(span.spanId, {
            ...span,
            children: [],
            depth: 0,
            descendantCount: 0,
        });
    }

    for (const span of spans) {
        const node = spanMap.get(span.spanId);
        if (node == null) continue;
        if (span.parentSpanId != null && spanMap.has(span.parentSpanId)) {
            const parent = spanMap.get(span.parentSpanId);
            if (parent != null) {
                parent.children.push(node);
            }
        } else {
            roots.push(node);
        }
    }

    const setDepthsAndCounts = (
        current: HierarchicalSpan,
        depth: number,
    ): number => {
        current.depth = depth;
        let count = 0;
        for (const child of current.children) {
            count += 1 + setDepthsAndCounts(child, depth + 1);
        }
        current.descendantCount = count;
        return count;
    };

    for (const root of roots) {
        setDepthsAndCounts(root, 0);
    }

    return roots;
}

function getTraceStartTime(spans: SpanData[]): number | null {
    const times = spans
        .filter(
            (s): s is SpanData & { startedAt: Date | string } =>
                s.startedAt != null,
        )
        .map((s) => new Date(s.startedAt).getTime());
    return times.length > 0 ? Math.min(...times) : null;
}

function getTraceEndTime(spans: SpanData[]): number | null {
    const times = spans
        .filter(
            (s): s is SpanData & { endedAt: Date | string } =>
                s.endedAt != null,
        )
        .map((s) => new Date(s.endedAt).getTime());
    return times.length > 0 ? Math.max(...times) : null;
}

function formatDuration(ms: number): string {
    if (ms < 1000) return `${Math.round(ms)}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
}
